// SEO-12 — /resources content layer.
//
// ONE registry for the public Resources library. The index (/resources) and every article route
// (/resources/<slug>) render from this module, and scripts/prerender.mjs discovers the article
// routes by crawling the rendered index — so prerender coverage can never drift from what ships.
//
// EDITORIAL RULES (enforced by resourceLibrary.test.js, documented in
// docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   1. Every factual claim is grounded in the repo — docs/, core/marketing.py, CLAUDE.md, or the
//      claims registry (docs/marketing/CLAIMS_REGISTRY.md). No invented statistics, adoption
//      counts, uptime figures, certifications, testimonials or "studies show".
//   2. An external regulatory fact is CITED, not paraphrased into a claim of ours: link the same
//      primary source the product already cites (see the /compliance/dpdp page).
//   3. Every article interlinks at least TWO /modules/<key> pages and exactly one conversion page.
//   4. Body length stays inside 700-1200 words. Below the floor it is a stub; above the ceiling it
//      is padding. Write less rather than filling.
//   5. AUTHORSHIP is organisational. We do not invent a human byline; a named byline requires a
//      real person to own the article (see the process doc's open item).
//
// Inline links inside prose use a [label](/path) token: an internal path renders as a router Link,
// an https:// URL as an external anchor. See ResourceProse.js.

import feeReconciliation from "./articles/feeReconciliation";
import dpdpForSchools from "./articles/dpdpForSchools";
import migrateWithoutParentNoise from "./articles/migrateWithoutParentNoise";
import erpEvaluationChecklist from "./articles/erpEvaluationChecklist";
import boardConfigurationNotForks from "./articles/boardConfigurationNotForks";
// 2026-08-18 second editorial pass — the remaining fifteen topics of the committed 20-topic
// roadmap (docs/marketing/RESOURCES_CONTENT_ROADMAP.md §2), each written from the grounding that
// roadmap row records. The roadmap's backlog is now empty.
import aiNearStudentRecords from "./articles/aiNearStudentRecords";
import chasingFeesWithoutHarassment from "./articles/chasingFeesWithoutHarassment";
import concessionsThatSurviveAudit from "./articles/concessionsThatSurviveAudit";
import consentAsEvidence from "./articles/consentAsEvidence";
import deleteStudentData from "./articles/deleteStudentData";
import designingFeeStructure from "./articles/designingFeeStructure";
import gstOnSchoolFees from "./articles/gstOnSchoolFees";
import attendanceAsEvidence from "./articles/attendanceAsEvidence";
import marksModeration from "./articles/marksModeration";
import payrollSchoolCanDefend from "./articles/payrollSchoolCanDefend";
import staffAccessGranted from "./articles/staffAccessGranted";
import timetableDraftNotDecision from "./articles/timetableDraftNotDecision";
import transportSeatSafety from "./articles/transportSeatSafety";
import udiseApaarReadiness from "./articles/udiseApaarReadiness";
import verifiableCertificates from "./articles/verifiableCertificates";

/** Organisational author for every published article. Never a fabricated human byline. */
export const RESOURCE_AUTHOR = Object.freeze({
  name: "Vidyapeeth360 Team",
  role: "Product and implementation team",
});

export const RESOURCES_BASE_PATH = "/resources";

/** Conversion destinations an article is allowed to close on (rule 3). */
export const CONVERSION_ROUTES = Object.freeze(["/pricing", "/contact", "/register"]);

/**
 * Topic labels. The order below is the index's section order, so it reads as a buyer's journey:
 * choose → collect money → stay compliant → move in → run the place → teach.
 * A new topic key must appear here BEFORE an article can use it (the contract test asserts the
 * article's `topic` string equals this row's label, so the two can never drift).
 */
export const RESOURCE_TOPICS = Object.freeze([
  { key: "buying", label: "Buying guide" },
  { key: "fees", label: "Fees & finance" },
  { key: "compliance", label: "Compliance" },
  { key: "implementation", label: "Implementation" },
  { key: "operations", label: "Staff & operations" },
  { key: "academics", label: "Academics" },
]);

// The deliberate READING order, not a timestamp sort — the whole library shipped across two
// editorial passes on the same date, so a date sort would be arbitrary. The index groups these by
// topic (in RESOURCE_TOPICS order) and preserves this order inside each group.
const ARTICLES = Object.freeze([
  erpEvaluationChecklist,
  aiNearStudentRecords,
  feeReconciliation,
  chasingFeesWithoutHarassment,
  concessionsThatSurviveAudit,
  designingFeeStructure,
  gstOnSchoolFees,
  dpdpForSchools,
  consentAsEvidence,
  deleteStudentData,
  udiseApaarReadiness,
  verifiableCertificates,
  migrateWithoutParentNoise,
  payrollSchoolCanDefend,
  staffAccessGranted,
  transportSeatSafety,
  boardConfigurationNotForks,
  attendanceAsEvidence,
  marksModeration,
  timetableDraftNotDecision,
]);

export const RESOURCE_ARTICLES = ARTICLES;

const bySlug = Object.freeze(
  ARTICLES.reduce((acc, article) => {
    acc[article.slug] = article;
    return acc;
  }, {}),
);

export const resourceBySlug = bySlug;

/** The canonical path for one article. */
export const resourcePath = (slug) => `${RESOURCES_BASE_PATH}/${slug}`;

/** Resolve an article by slug; returns null for an unknown slug (the page renders the 404). */
export function resourceArticleFor(slug) {
  const key = typeof slug === "string" ? slug.trim().toLowerCase() : "";
  return Object.prototype.hasOwnProperty.call(bySlug, key) ? bySlug[key] : null;
}

/**
 * The library grouped for the index, in RESOURCE_TOPICS order, preserving the reading order inside
 * each group. A topic with no published article is omitted, so adding a topic key ahead of its
 * first article renders nothing rather than an empty section.
 */
export function resourcesByTopic() {
  return RESOURCE_TOPICS
    .map((topic) => ({ ...topic, articles: ARTICLES.filter((a) => a.topicKey === topic.key) }))
    .filter((group) => group.articles.length > 0);
}

/** Up to `n` other articles, preferring the same topic — the "keep reading" rail. */
export function relatedResources(article, n = 3) {
  if (!article) return ARTICLES.slice(0, n);
  const others = ARTICLES.filter((item) => item.slug !== article.slug);
  const sameTopic = others.filter((item) => item.topicKey === article.topicKey);
  const rest = others.filter((item) => item.topicKey !== article.topicKey);
  return [...sameTopic, ...rest].slice(0, n);
}

// ---------------------------------------------------------------------------
// Text extraction — one implementation shared by the word-count contract test, the SEO
// description fallback and the reading-time figure, so those three can never disagree.
// ---------------------------------------------------------------------------

/** Strip the inline token syntax so a count measures WORDS, not markup. */
export function plainText(value) {
  return String(value || "")
    // Function replacements, not "$N" strings: the India-first currency guard
    // (lib/financeCurrencyGuard.test.js) scans production source for visible dollar
    // labels, and a "$1" backreference reads as one. Keep the guard strict.
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_match, label) => label) // [label](/path) -> label
    .replace(/\*\*([^*]+)\*\*/g, (_match, inner) => inner) // **bold** -> bold
    .replace(/\s+/g, " ")
    .trim();
}

/** Every human-readable string in an article, in reading order. */
export function articleTextParts(article) {
  const parts = [];
  if (!article) return parts;
  if (article.standfirst) parts.push(article.standfirst);
  for (const section of article.sections || []) {
    if (section.heading) parts.push(section.heading);
    for (const block of section.blocks || []) {
      if (block.text) parts.push(block.text);
      if (block.title) parts.push(block.title);
      for (const item of block.items || []) parts.push(item);
    }
  }
  return parts;
}

export function articleWordCount(article) {
  const text = articleTextParts(article).map(plainText).join(" ").trim();
  return text ? text.split(/\s+/).length : 0;
}

/** Every internal path an article links to, from prose tokens AND its structured link fields. */
export function articleInternalLinks(article) {
  const links = new Set();
  if (!article) return [];
  for (const key of article.moduleLinks || []) links.add(`/modules/${key}`);
  if (article.conversion?.to) links.add(article.conversion.to);
  for (const part of articleTextParts(article)) {
    const token = /\[[^\]]+\]\((\/[^)\s]+)\)/g;
    for (let m = token.exec(part); m; m = token.exec(part)) links.add(m[1]);
  }
  return [...links];
}

/** Every external URL an article links to, from prose tokens AND its sources list. */
export function articleExternalLinks(article) {
  const links = new Set();
  if (!article) return [];
  for (const source of article.sources || []) if (source.href) links.add(source.href);
  for (const part of articleTextParts(article)) {
    const token = /\[[^\]]+\]\((https?:\/\/[^)\s]+)\)/g;
    for (let m = token.exec(part); m; m = token.exec(part)) links.add(m[1]);
  }
  return [...links];
}
